import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Ticket } from 'src/app/models/core/ticket.model';
import { ConfirmCloneModalComponent } from '../confirm-clone-modal/confirm-clone-modal.component';
import { ConfirmVentaRapidaCloneModalComponent } from '../confirm-venta-rapida-clone-modal/confirm-venta-rapida-clone-modal.component';
import { VentaRapidaCloneContext } from 'src/app/models/venta-rapida/venta-rapida-clone-context.model';
import { head } from 'lodash';

@Component({
  selector: 'app-share-direct-ticket-modal',
  templateUrl: './share-direct-ticket-modal.component.html',
  styleUrls: ['./share-direct-ticket-modal.component.scss'],
  standalone: false
})
export class ShareDirectTicketModalComponent implements OnInit {
  isNative = false;
  animatedLink = '';
  typingInterval: any;
  @Input() ticket!: Ticket;
  @Input() ventaRapidaContext?: VentaRapidaCloneContext;

  constructor(
    private modalCtrl: ModalController,
    private readonly _translate: TranslateService,
    private customToast: CustomToastService,
  ) { }

  ngOnInit() {
    this.isNative = Capacitor.isNativePlatform();
    this.animatedLink = '';
  }

  closeModal() {
    this.modalCtrl.dismiss();
  }

  messageQuota(): string {
    return (
      'Link habilitado para registrar ' +
      this.ticket.quota +
      ' asistente/s. ' +
      this.ticket.product.share_message
    );
  }

  get messageToShare(): string {
    const { min_age } = this.ticket.product;

    let message = this.messageQuota();

    if (min_age && min_age !== 0) {
      message += ` Producto habilitado para mayores de ${min_age} años.`;
    }

    return message;
  }


  get ticketLink(): string {
    const assistantUuid = this.ventaRapidaContext?.assistantUuid;
    if (assistantUuid && this.ticket?.uuid) {
      return this.buildEcommerceUrl(
        `ticket/${this.ticket.uuid}/assistant/${assistantUuid}`,
      );
    }
    return `${environment.ecommerceDomain}/ticket-verification/${this.ticket?.uuid}`;
  }

  private buildEcommerceUrl(path: string): string {
    const base = environment.ecommerceDomain.replace(/\/$/, '');
    return `${base}/${path}`;
  }

  get fullShareMessage(): string {
    return `${this.messageToShare}\n\n${this.ticketLink}`;
  }

  copyMessageToClipboard(successKey: string = 'share-ticket-modal.copy-message-success') {
    navigator.clipboard.writeText(this.fullShareMessage).then(() => {
      this.customToast.show(this._translate.instant(successKey), 3000, 'success');
    }).catch(() => {
      this.customToast.show(this._translate.instant('share-ticket-modal.copy-message-error'), 3000, 'error');
    });
  }

  async copyToClipboard() {
    const message = this.messageToShare;
    const link = this.ticketLink;

    const htmlContent = `
      <p style="color: #000; white-space: pre-wrap;">${message}</p>
      <p><a href="${link}" target="_blank" style="color: #007aff; text-decoration: underline;">${link}</a></p>
    `;

    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlContent;
    document.body.appendChild(tempElement);

    const range = document.createRange();
    range.selectNode(tempElement);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);

    const copied = document.execCommand('copy');
    document.body.removeChild(tempElement);

    if (copied) {
      this.customToast.show(this._translate.instant('share-ticket-modal.copy-message-success'), 3000, 'success');
    } else {
      this.customToast.show(this._translate.instant('share-ticket-modal.copy-message-error'), 3000, 'error');
    }
  }

  shareWhatsApp() {
    window.open(`https://wa.me?text=${encodeURIComponent(this.fullShareMessage)}`, '_blank');
  }

  shareEmail() {
    const productName = this.ticket?.product.name ?? 'evento';
    const subject = this._translate.instant('share-ticket-modal.email-subject', { productName });
    const body = encodeURIComponent(this.fullShareMessage);
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;

    window.open(mailtoUrl, '_blank');
  }

  shareInstagram() {
    this.copyMessageToClipboard('share-ticket-modal.instagram-copy-success');

    setTimeout(() => {
      window.open('https://www.instagram.com/accounts/login/?next=/', '_blank');
    }, 2100);
  }

  async shareNative() {
    try {
      await Share.share({
        title: 'Compartir entrada',
        text: this.fullShareMessage,
        dialogTitle: 'Compartir con...'
      });
    } catch (error) {
      console.error('Error al compartir:', error);
    }
  }

  async cloneTicket() {
    const modal = await this.modalCtrl.create({
      component: ConfirmCloneModalComponent,
      componentProps: { ticket: this.ticket },
      cssClass: 'custom-modal',
      showBackdrop: true,
      backdropDismiss: false,
    });
    await modal.present();
    const { data, role } = await modal.onDidDismiss();
    if (role === 'cloned' && data?.newTicket) {
      this.ticket = data.newTicket;
      this.startTypingEffect();
    }
  }

  async cloneVentaRapidaTicket() {
    if (!this.ventaRapidaContext) return;
    const modal = await this.modalCtrl.create({
      component: ConfirmVentaRapidaCloneModalComponent,
      componentProps: {
        ticket: this.ticket,
        ventaRapidaContext: this.ventaRapidaContext,
      },
      cssClass: 'custom-modal',
      showBackdrop: true,
      backdropDismiss: false,
    });
    await modal.present();
    const { data, role } = await modal.onDidDismiss();
    if (role === 'cloned' && data?.newTicket) {
      this.ticket = data.newTicket;
      if (data.assistantUuid && this.ventaRapidaContext) {
        this.ventaRapidaContext = {
          ...this.ventaRapidaContext,
          assistantUuid: data.assistantUuid,
        };
      }
      this.startTypingEffect();
    }
  }

  startTypingEffect() {
    const fullText = this.ticketLink;
    this.animatedLink = '';
    clearInterval(this.typingInterval);

    let i = 0;
    this.typingInterval = setInterval(() => {
      this.animatedLink += fullText[i];
      i++;
      if (i >= fullText.length) {
        clearInterval(this.typingInterval);
      }
    }, 15);
  }

}
